import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import './SecondaryTable.css';
import type { TableProps } from '@frontend/Models/Table/TableModels';
import Pagination from '../Pagination/Pagination';

interface ExtendedTableProps<T> extends Omit<TableProps<T>, 'data' | 'rowKey'> {
  data: T[] | null;
  rowKey?: keyof T | string;
}

function Table<T extends Record<string, any>>({ 
  columns, 
  data, 
  emptyMessage = "No data found",
  emptyAction,
  rowKey = "id", // default key for React list rendering
  pagination
}: ExtendedTableProps<T>): JSX.Element | null {
  if (!data) {
    return null;
  }

  if (data.length === 0) {
    return (
      <>
        <h3 className="secondary-no-data">{emptyMessage}</h3>
        {emptyAction && (
          <div className="secondary-center-container">
            {emptyAction}
          </div>
        )}
      </>
    );
  }

  return (
    <div className="secondary-table-container">
      <table>
        <thead>
          <tr>
            {columns.map((column, index) => (
              <th key={index}>{column.header}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.map((row, index) => (
            <tr key={row[rowKey as string] || index}>
              {columns.map((column, colIndex) => (
                <td key={colIndex}>
                  {column.render ? (
                    column.render(row)
                  ) : (
                    <div className="secondary-d-flex">
                      {column.icon && (
                        <FontAwesomeIcon 
                          icon={column.icon} 
                          className="secondary-tableIcon" 
                        />
                      )}
                      <span>{column.accessor ? row[column.accessor] : null}</span>
                    </div>
                  )}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {pagination && (
        <Pagination
          currPage={pagination.currPage}
          onPageChange={pagination.onPageChange}
          pageLimit={pagination.pageLimit}
          pageSize={pagination.pageSize}
          onPageSizeChange={pagination.onPageSizeChange}
        />
      )}
    </div>
  );
}

export default Table;

